<template>
  <section
    class="relative overflow-hidden max-[1023px]:pb-5 md:pb-[50px]"
    :class="
      showWaves
        ? 'pt-[114px] pb-[114px] min-[1024px]:pb-[110px]'
        : 'pt-[102px] max-[1023px]:pt-[41px] min-[1024px]:pb-[158px]'
    "
  >
    <SectionWaves v-if="showWaves" />
    <div
      class="relative px-4"
      :class="showWaves ? 'bg-gray-100 py-12 md:py-16' : ''"
    >
      <div class="w-full max-w-container z-10 mx-auto">
        <h2
          class="mb-0 mt-0 md:mt-10 font-extrabold landing_h2 flex w-full items-center justify-center gap-2 text-center"
        >
          <span aria-hidden="true">💬</span>
          <template v-if="isTradelineBrokerage">
            <Translate translate-key="landing_testimonials.title" />
          </template>
          <template v-else>
            {{ titleKey }}
          </template>
        </h2>
        <ul
          class="mx-0 mt-10 mb-[88px] grid min-h-[966px] w-auto list-none grid-cols-1 gap-6 p-0 md:min-h-[327px] md:grid-cols-3"
        >
          <li
            v-for="(item, index) in displayedTestimonials"
            :key="item.id ?? index"
            class="relative rounded-xl bg-white p-6 py-1 mx-1 md:mx-0 md:py-15 pb-5 pt-[62px] text-left shadow-xl md:pb-[68px]"
          >
            <ul
              class="user mb-9 flex w-full list-none flex-row items-center justify-between gap-4 p-0"
            >
              <li>
                <h3 class="m-0 font-bold text-[#333] text-base">
                  {{ item.name }}
                </h3>
              </li>
              <li>
                <NuxtImg
                  v-if="item.img_local"
                  format="webp"
                  width="77"
                  height="77"
                  :src="item.img_local"
                  :alt="alt"
                  loading="lazy"
                  class="h-[77px] w-[77px] rounded-full"
                />
              </li>
            </ul>
            <p class="mb-0 text-[#666]">{{ item.text }}</p>
            <a
              v-if="item.link"
              :href="item.link"
              target="_blank"
              rel="noopener noreferrer"
              class="absolute bottom-0 right-0 flex h-[68px] w-[58px] items-center justify-center text-lg font-bold leading-[68px] text-[rgba(134,134,134,0.8)]"
              aria-label="External link"
            >
              f
            </a>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import SectionWaves from "~/features/landing-page/components/sections/SectionWaves.vue";

export interface TestimonialItem {
  name: string;
  text: string;
  img_local?: string;
  link?: string;
  id?: number;
}

const props = withDefaults(
  defineProps<{
    userType?: number;
    testimonialsData: TestimonialItem[];
    /** Show wave decoration above/below the section */
    showWaves?: boolean;
    /** Max number of testimonials to show (default 3) */
    maxItems?: number;
  }>(),
  { userType: 1, showWaves: false, maxItems: 3 },
);

const route = useRoute();
const { t } = useI18n();

const displayedTestimonials = computed(() =>
  props.testimonialsData.slice(0, props.maxItems),
);

const isTradelineBrokerage = computed(() =>
  route.path.includes("tradeline-brokerage"),
);
const isSellingTradelines = computed(() =>
  route.path.includes("selling-tradelines"),
);

const titleKey = computed(() =>
  props.userType === 1
    ? t("landing_testimonials.testimonials")
    : t("landing_testimonials.sellers_feedback"),
);

const alt = computed(() => t("landing_testimonials.alt"));
</script>
